import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { FONT } from "@/lib/tokens";
import { appleEase } from "@/lib/motion";

const CSS = `
  .tg-standard {
    background-color: #F5F5F7;
    padding: 120px 22px;
    display: flex;
    flex-direction: column;
    align-items: center;
    font-family: ${FONT};
    color: #1A1A1A;
    overflow: hidden;
  }

  .tg-standard-inner {
    max-width: 1200px;
    width: 100%;
  }

  /* ── Header ── */
  .tg-standard-header {
    text-align: center;
    margin-bottom: 60px;
    padding: 0 20px;
  }

  .tg-standard-eyebrow {
    font-size: clamp(18px, 2vw, 19.5px);
    font-weight: 600;
    line-height: 1.2;
    letter-spacing: -0.01em;
    text-transform: none;
    /* Couleur unique des labels de l'accueil : un noir adouci, un cran plus
       clair que le #1D1D1F des titres. Assez proche pour rester sobre, assez
       distinct pour que le label ne se confonde pas avec le titre du dessous. */
    color: #3A3A3C;
    margin-bottom: 16px;
    display: block;
  }

  .tg-standard-title {
    font-size: clamp(2.2rem, 5vw, 3.2rem);
    font-weight: 700;
    line-height: 1.1;
    letter-spacing: -0.02em;
    margin-bottom: 16px;
    color: #1D1D1F;
  }

  .tg-standard-subtitle {
    font-size: clamp(16px, 3vw, 19px);
    color: #6E6E73;
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.42;
  }

  /* ── Grid ── */
  .tg-standard-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
    width: 100%;
  }

  /* Points de pagination — masqués sur desktop (grille), visibles en mobile
     (carrousel). Alignés sur ceux de la section "Le travail évolue". */
  .tg-standard-dots {
    display: none;
    gap: 10px;
    justify-content: center;
    margin-top: 28px;
  }
  .tg-standard-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #D2D2D7;
    border: none;
    padding: 0;
    cursor: pointer;
    transition: all 0.4s ease;
  }
  .tg-standard-dot--active {
    background: #86868B;
    transform: scale(1.2);
  }

  /* ── Card (Apple-style) ── */
  .tg-standard-card {
    border-radius: 28px;
    overflow: hidden;
    aspect-ratio: 2 / 3;
    display: flex;
    flex-direction: column;
    box-shadow: 0 4px 12px rgba(0,0,0,0.03);
    transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    text-decoration: none;
    background: #FFFFFF;
  }

  .tg-standard-card:hover {
    transform: translateY(-4px) scale(1.01);
    box-shadow: 0 20px 40px rgba(0,0,0,0.08);
  }

  .tg-standard-card-top {
    padding: 48px;
    position: relative;
    z-index: 10;
  }

  .tg-standard-card-label {
    font-size: 17.5px;
    font-weight: 600;
    text-transform: none;
    letter-spacing: -0.01em;
    color: #86868B;
    margin-bottom: 12px;
    display: block;
  }

  .tg-standard-card-hook {
    font-size: clamp(22px, 4vw, 28px);
    font-weight: 600;
    line-height: 1.14;
    letter-spacing: 0.007em;
    color: #1D1D1F;
    margin: 0;
  }

  /* Special handling for cards with dark backgrounds */
  .card-competence {
    background: #000000 !important;
  }
  .card-competence .tg-standard-card-hook {
    color: #F5F5F7;
  }
  .card-competence .tg-standard-card-label {
    color: #A1A1A6;
  }

  .tg-standard-card-bottom {
    flex: 1;
    position: relative;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    width: 100%;
    overflow: hidden;
  }

  /* Image as Background for Competence & System */
  .card-competence .tg-standard-card-bottom,
  .card-system .tg-standard-card-bottom {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    z-index: 1;
  }

  .tg-standard-card-img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: bottom center;
    transition: transform 0.5s ease;
  }

  /* Card Specifics */
  .card-system .tg-standard-card-img {
    object-fit: cover;
    object-position: center;
  }

  .card-licence .tg-standard-card-img {
    transform: scale(1.2);
    transform-origin: bottom center;
  }

  .card-competence .tg-standard-card-img {
    object-fit: cover;
    object-position: center;
    opacity: 0.9;
  }

  @media (max-width: 1024px) {
    .tg-standard { padding: 80px 24px; }
    .tg-standard-grid { grid-template-columns: 1fr; }
    .tg-standard-card { max-width: 420px; width: 100%; margin: 0 auto; }
    .tg-standard-card-top { padding: 32px; }
  }

  /* ── Tablette (769–1024px) : carrousel horizontal, comme le mobile.
        La règle ≤1024px passait la grille sur 1 colonne : les 3 cards
        s'empilaient sur une hauteur interminable, chacune bridée à 420px et
        perdue au milieu d'un écran large. Ici on garde 2 cards de front avec
        l'amorce de la 3e. Ce bloc suit le ≤1024px pour l'écraser, et ne
        recouvre ni le mobile (≤768px) ni le desktop (≥1025px). ── */
  @media (min-width: 769px) and (max-width: 1024px) {
    .tg-standard { padding: 80px 0; }
    .tg-standard-inner { max-width: 100%; }
    .tg-standard-header { margin-bottom: 44px; padding: 0 40px; }

    .tg-standard-grid {
      display: flex;
      grid-template-columns: none;
      gap: 20px;
      width: 100%;
      overflow-x: auto;
      overflow-y: hidden;
      scroll-snap-type: x mandatory;
      scroll-padding-left: 40px;
      scroll-behavior: smooth;
      -webkit-overflow-scrolling: touch;
      padding: 4px 40px;
      scrollbar-width: none;
    }
    .tg-standard-grid::-webkit-scrollbar { display: none; }

    .tg-standard-card {
      flex: 0 0 46%;
      max-width: none;
      width: auto;
      margin: 0;
      scroll-snap-align: start;
    }
    .tg-standard-card-top { padding: 30px; }

    .tg-standard-dots { display: flex; }
  }

  /* ── Mobile : la grille devient un carrousel horizontal (snap + peek),
        façon Apple mobile, aligné sur la section "Le travail évolue". ── */
  @media (max-width: 768px) {
    .tg-standard { padding: 72px 0; }
    .tg-standard-inner { max-width: 100%; }
    .tg-standard-header { margin-bottom: 40px; padding: 0 24px; }

    .tg-standard-grid {
      display: flex;
      grid-template-columns: none;
      gap: 16px;
      width: 100%;
      overflow-x: auto;
      overflow-y: hidden;
      scroll-snap-type: x mandatory;
      scroll-behavior: smooth;
      -webkit-overflow-scrolling: touch;
      /* Memes reglages que les deux autres rails de l'accueil (galerie
         d'exemples et aiguillage) : 22 px avant la premiere carte, cartes
         a 86 %, calage a gauche. En 78vw centres, celles-ci etaient
         nettement plus etroites que les autres sur la meme page. */
      padding: 4px 20px;
      scroll-padding-left: 20px;
      scrollbar-width: none;
    }
    .tg-standard-grid::-webkit-scrollbar { display: none; }
    .tg-standard-grid::after { content: ""; flex: 0 0 8px; }

    .tg-standard-card {
      flex: 0 0 92%;
      max-width: none;
      width: auto;
      margin: 0;
      scroll-snap-align: start;
    }
    .tg-standard-card-top { padding: 28px; }

    /* Seule carte en "contain" : a cette largeur son visuel flottait au
       milieu du blanc, alors que les deux autres remplissent la leur. */
    .card-licence .tg-standard-card-img { transform: scale(1.5); }

    .tg-standard-dots { display: flex; }
  }
`;

export default function StandardSection() {
  const reveal = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.8, ease: appleEase }
  };

  const cards = [
    {
      id: "competence",
      label: "Compétence",
      title: "Votre savoir-faire existe déjà. Il n’attend qu’une structure.",
      bgClass: "bg-grad-1",
      delay: 0.2,
      img: "/images/tangig-competence.webp"
    },
    {
      id: "system",
      label: "Système",
      title: "Un actif. Clair. Documenté. Prêt.",
      bgClass: "bg-grad-system",
      delay: 0.3,
      img: "/images/tangig-system-homepage.webp"
    },
    {
      id: "licence",
      label: "Licence",
      title: "Vous percevez un revenu. Chaque fois qu’il est exploité.",
      bgClass: "bg-solid-3",
      delay: 0.4,
      img: "/images/tangig-licence.webp"
    }
  ];

  const [activeIdx, setActiveIdx] = useState(0);
  const gridRef = useRef(null);

  const handleScroll = () => {
    const el = gridRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    if (max <= 0) return;
    const idx = Math.round((el.scrollLeft / max) * (cards.length - 1));
    if (idx !== activeIdx) setActiveIdx(idx);
  };

  const scrollTo = (idx) => {
    const el = gridRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    el.scrollTo({ left: (idx / (cards.length - 1)) * max, behavior: "smooth" });
  };

  return (
    <>
      <style>{CSS}</style>
      <section className="tg-standard">
        <div className="tg-standard-inner">
          
          <header className="tg-standard-header">
            <motion.span className="tg-standard-eyebrow" {...reveal}>
              Revenu récurrent
            </motion.span>
            <motion.h2 className="tg-standard-title" {...reveal} transition={{ delay: 0.1 }}>
              Des systèmes opérationnels.
            </motion.h2>
            <motion.p className="tg-standard-subtitle" {...reveal} transition={{ delay: 0.2 }}>
              Activables immédiatement pour générer du revenu.
            </motion.p>
          </header>

          <div className="tg-standard-grid" ref={gridRef} onScroll={handleScroll}>
            {cards.map((card, i) => (
              <motion.div 
                key={i}
                className={`tg-standard-card ${card.bgClass} card-${card.id}`}
                {...reveal}
                transition={{ duration: 0.8, ease: appleEase, delay: card.delay }}
              >
                <div className="tg-standard-card-top">
                  <span className="tg-standard-card-label">{card.label}</span>
                  <h3 className="tg-standard-card-hook">{card.title}</h3>
                </div>
                <div className="tg-standard-card-bottom">
                  {card.img && (
                    <img 
                      src={card.img} 
                      alt={card.label} 
                      className="tg-standard-card-img"
                    />
                  )}
                </div>
              </motion.div>
            ))}
          </div>

          <div className="tg-standard-dots">
            {cards.map((_, i) => (
              <button
                key={i}
                type="button"
                className={`tg-standard-dot ${i === activeIdx ? "tg-standard-dot--active" : ""}`}
                onClick={() => scrollTo(i)}
                aria-label={`Aller à la carte ${i + 1}`}
              />
            ))}
          </div>

        </div>
      </section>
    </>
  );
}